import {Helmet, HelmetProvider} from '@dr.pogodin/react-helmet';
import {i18n} from '@online-offer/i18n';
import {NotFoundPage} from '@online-offer/shared';
import {skipToken} from '@reduxjs/toolkit/query';
import {Box, Center, Heading, Image, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {ErrorSection} from '../../(sections)/ErrorSection/ErrorSection';
import {Loading} from '../../(sections)/Loading/Loading';
import {
  useGetBusinessCaseOnlineOfferQuery,
  useListLogosQuery,
  useRefuseOnlineOfferCommunicationMutation,
} from '../../api/onlineOfferApi';
import {Button} from '../../components/Button/Button';
import {CardOo} from '../../components/CardOo/CardOo';
import {APPLICATION_NAME} from '../../consts/applicationName';
import {queryParams} from '../../consts/queryParams';
import {getWorkspaceCustomization} from '../../utils/getWorkspaceCustomization';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {makeStorageResizeUrl} from '../../utils/makeStorageResizeUrl';
import {translateWithFallback as t} from '../../utils/translateWithFallback';
import {useQueryState} from '../../utils/useQueryState';

type UnsubscribeState = 'idle' | 'loading' | 'done' | 'error' | 'notFound';
const LOGO_KEY = 'tenant-logo_digital-deal';
const LOGO_WIDTH = 440;
const LOGO_HEIGHT = 88;

const isNotFoundError = (error: unknown) =>
  isNotNil(error) &&
  typeof error === 'object' &&
  'status' in error &&
  (error.status === 400 || error.status === 404);

export function UnsubscribePage() {
  const {offerId} = useParams<{offerId: string}>();
  const [sellerEmail] = useQueryState(queryParams.SELLER_EMAIL);
  const {workspace} = getWorkspaceFromUri();
  const offer = useGetBusinessCaseOnlineOfferQuery(
    isNotNil(offerId) && isNotNil(workspace) ? {offerId, workspace} : skipToken
  );
  const {logo} = getWorkspaceCustomization();
  const {data} = useListLogosQuery({'X-Workspace': workspace ?? ''}, {skip: isNil(workspace)});
  const [refuseCommunication] = useRefuseOnlineOfferCommunicationMutation();
  const [state, setState] = useState<UnsubscribeState>('idle');
  const logoUri = data?.logos[LOGO_KEY]?.uri;
  const resizedLogo = isNotNil(logoUri)
    ? makeStorageResizeUrl(logoUri, {
        width: LOGO_WIDTH,
        height: LOGO_HEIGHT,
        withoutEnlargement: true,
      })
    : null;

  const confirm = () => {
    if (state !== 'idle' || isNil(offerId) || isNil(workspace)) {
      return;
    }
    setState('loading');
    refuseCommunication({offerId, workspace})
      .unwrap()
      .then(() => setState('done'))
      .catch((error: unknown) => setState(isNotFoundError(error) ? 'notFound' : 'error'));
  };

  return (
    <HelmetProvider>
      <Helmet titleTemplate={`%s • ${APPLICATION_NAME}`} htmlAttributes={{lang: i18n.language}}>
        <title>{t('unsubscribe.title', 'Stop receiving automatic after-sale messages?')}</title>
      </Helmet>
      {match({
        isNotFound: isNotFoundError(offer.error) || state === 'notFound',
        isErrored: offer.isError || state === 'error',
        isLoading: offer.isLoading || isNil(offer.data),
        state,
      })
        .with({isNotFound: true}, () => (
          <NotFoundPage sellerEmail={sellerEmail} ContactSellerButton={Button} />
        ))
        .with({isErrored: true}, () => <ErrorSection />)
        .with({isLoading: true}, () => <Loading />)
        .otherwise(({state: currentState}) => (
          <Center>
            <Box padding={6} width="100%" maxWidth={120}>
              <VStack spacing={6} align="center">
                {isNotNil(resizedLogo) ? <Image src={resizedLogo} maxHeight={11} /> : logo}
                <CardOo>
                  {match(currentState)
                    .with('done', () => (
                      <Text data-testid="unsubscribe-done">
                        {t(
                          'unsubscribe.done',
                          'Done. We will not send you any more automatic after-sale messages.'
                        )}
                      </Text>
                    ))
                    .with('idle', 'loading', () => (
                      <VStack spacing={4}>
                        <Heading size={2}>
                          {t('unsubscribe.title', 'Stop receiving automatic after-sale messages?')}
                        </Heading>
                        <Text>
                          {t(
                            'unsubscribe.message',
                            'Press the button and we will no longer send you the automatic after-sale messages about your purchase (the thank-you and the check-in).'
                          )}
                        </Text>
                        <Button
                          variant="primary"
                          title={t('unsubscribe.confirm', 'Stop automatic after-sale messages')}
                          onClick={confirm}
                          isLoading={currentState === 'loading'}
                          data-testid="unsubscribe-confirm"
                        />
                      </VStack>
                    ))
                    .otherwise(() => null)}
                </CardOo>
              </VStack>
            </Box>
          </Center>
        ))}
    </HelmetProvider>
  );
}
